// apps/admin/src/queries/supportTicketKeys.ts
//
// Phase 8.2 Wave 8 Task 53 — TanStack Query key factory for the Support
// Tickets admin surface. Mirrors adminRoleKeys.ts / adminUserKeys.ts
// hierarchical pattern so callers invalidate at the right granularity:
//
//   ['support','tickets']                       — wipe everything
//   ['support','tickets','list']                — all list slices
//   ['support','tickets','list', filters]       — one filter fingerprint
//   ['support','tickets','detail', id]          — detail page (task-54)
//   ['support','tickets','messages', id]        — message thread (task-54)
//   ['support','tickets','stats']               — dashboard counts (task-55)
//
// Pure factory — no hooks, no axios. Safe to import from any layer.
//
// vg-binding: PLAN:task-53
// vg-binding: P8.D-103   — list cache slice fingerprint (column-driven)
// vg-binding: P8.D-200   — canonical D-200 schema (D-223 deprecated)
//
// Phase 9.3 Wave 7 Task 13 — extended with agent-config + routing-rule
// query keys (P9.3.D-07, P9.3.D-08) for the Support Agent Assignment
// surface. Additive only — existing supportTicketKeys consumers unchanged.
//
// vg-binding: PLAN:task-13
// vg-binding: P9.3.D-07   — agents / agentConfig cache slices
// vg-binding: P9.3.D-08   — routing-rules cache slice

import type { QueryClient } from '@tanstack/react-query';

import type { ListAgentsFilters } from '../api/support-agents.api.js';
import type { SupportTicketListFilters } from '../api/support-tickets.api.js';

/** Hierarchical query-key factory — read-only tuples for stable cache fingerprints. */
export const supportTicketKeys = {
  /** Root — invalidates every Support Tickets cache slice. */
  all: () => ['support', 'tickets'] as const,

  /** All list slices regardless of filter. */
  lists: () => ['support', 'tickets', 'list'] as const,

  /** One filter fingerprint — the canonical cache slice for the table. */
  list: (filters: SupportTicketListFilters) =>
    ['support', 'tickets', 'list', filters] as const,

  /** All detail slices regardless of id. */
  details: () => ['support', 'tickets', 'detail'] as const,

  /** Single ticket detail (consumed by task-54). */
  detail: (id: string) => ['support', 'tickets', 'detail', id] as const,

  /** Per-ticket message thread (consumed by task-54). */
  messages: (id: string) => ['support', 'tickets', 'messages', id] as const,

  /** Per-ticket dispatch list — active dispatch derived on reload
   *  (fix/dispatch-thread-reload). */
  dispatches: (id: string) =>
    ['support', 'tickets', 'dispatches', id] as const,

  /** Dashboard-wide stats (consumed by task-55). */
  stats: () => ['support', 'tickets', 'stats'] as const,

  // --- Phase 9.3 (agent assignment) ---------------------------------------

  /** All agent-roster list slices regardless of filter. */
  agentsRoot: () => ['support', 'agents'] as const,

  /** Agent roster list — one filter fingerprint (AgentsTab, task-17). */
  agents: (filters: ListAgentsFilters = {}) =>
    ['support', 'agents', 'list', filters] as const,

  /** Single agent config slice (AgentConfigModal, task-14). */
  agentConfig: (userId: string) =>
    ['support', 'agents', 'config', userId] as const,

  /** Routing-rule list (RoutingRuleEditor, task-16). */
  routingRules: () => ['support', 'routing-rules'] as const,
} as const;

export type SupportTicketListQueryKey = ReturnType<typeof supportTicketKeys.list>;
export type SupportAgentsQueryKey = ReturnType<typeof supportTicketKeys.agents>;
export type SupportRoutingRulesQueryKey = ReturnType<
  typeof supportTicketKeys.routingRules
>;
export type SupportTicketDetailQueryKey = ReturnType<
  typeof supportTicketKeys.detail
>;

/**
 * Invalidate every Support Tickets list + (optionally) one detail slice
 * + the stats card. Used by mutation hooks landing in task-54 / task-55
 * (reply, assign, escalate, close, reopen).
 */
export function invalidateSupportTicketCaches(
  qc: QueryClient,
  ticketId?: string,
): void {
  void qc.invalidateQueries({ queryKey: supportTicketKeys.lists() });
  void qc.invalidateQueries({ queryKey: supportTicketKeys.stats() });
  if (ticketId) {
    void qc.invalidateQueries({ queryKey: supportTicketKeys.detail(ticketId) });
    void qc.invalidateQueries({ queryKey: supportTicketKeys.messages(ticketId) });
  }
}

/**
 * Invalidate agent-roster + (optionally) one agent-config slice after an
 * add/config/remove/availability mutation (Phase 9.3, tasks 14–18).
 */
export function invalidateSupportAgentCaches(
  qc: QueryClient,
  userId?: string,
): void {
  void qc.invalidateQueries({ queryKey: supportTicketKeys.agentsRoot() });
  if (userId) {
    void qc.invalidateQueries({
      queryKey: supportTicketKeys.agentConfig(userId),
    });
  }
}

/** Invalidate the routing-rule list after a create/update/delete (task-16). */
export function invalidateSupportRoutingRuleCaches(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: supportTicketKeys.routingRules() });
}
